import { Button as BaseButton, type ButtonProps } from '@/components/button';
import { Clock, ListChecks, Timer } from 'lucide-react';
import { QuizPlayerScreen } from './quiz-player-screen';
import type { QuizPlayerWelcomeProps } from '../types';

import { cn } from '@/lib/utils';
import { useTranslation } from '@/providers/i18n';

type QuizPlayerWelcomeScreenProps = React.ComponentProps<'div'> &
  QuizPlayerWelcomeProps & {
    slots?: { Button?: React.ElementType<ButtonProps> };
    slotProps?: { button?: ButtonProps };
  };

export function QuizPlayerWelcomeScreen({
  title,
  description,
  questions = [],
  onStart = () => {},
  onCancel = () => {},
  slots = {},
  slotProps,
  details: customDetails = [],
}: QuizPlayerWelcomeScreenProps) {
  const { t } = useTranslation();
  const { Button = BaseButton } = slots;
  const totalTimeLimit = questions.reduce(
    (acc, q) => acc + (q.timeLimit || 0),
    0
  );
  const avgTimePerQuestion = Math.round(totalTimeLimit / questions.length);

  const details: { icon: React.ReactNode; label: React.ReactNode }[] = [
    {
      icon: <ListChecks size={20} />,
      label: t('questions', {
        fallback: '{value} Domande',
        args: { value: questions.length },
      }),
    },
  ];

  if (avgTimePerQuestion) {
    details.push({
      icon: <Clock size={20} />,
      label: t('quizTimeLimit', {
        fallback: 'Circa {value} secondi per domanda.',
        args: { value: avgTimePerQuestion },
      }),
    });
  }

  if (totalTimeLimit) {
    details.push({
      icon: <Timer size={20} />,
      label: t('quizTimeLimit', {
        fallback: 'Tempo totale: {value} minuti.',
        args: { value: totalTimeLimit / 60 },
      }),
    });
  }

  return (
    <QuizPlayerScreen variant="welcome">
      <div className="text-3xl font-bold mb-4 font-brand">{title}</div>
      <p className="mb-8">{description}</p>

      <div className="space-y-4 mb-8">
        {details.concat(customDetails).map((item, i) => (
          <div
            key={i}
            className="flex items-center gap-3 text-muted-foreground"
          >
            {item.icon}
            <span>{item.label}</span>
          </div>
        ))}
      </div>

      <div className="flex gap-2 items-center">
        <Button
          {...slotProps?.button}
          size="lg"
          variant="brand"
          onClick={onStart}
          className={cn('capitalize', slotProps?.button?.className)}
        >
          {t('start', 'Inizia')}
        </Button>
        <Button
          {...slotProps?.button}
          variant="link"
          className={cn(
            'capitalize text-muted-foreground',
            slotProps?.button?.className
          )}
          onClick={onCancel}
        >
          {t('cancel', 'Annulla')}
        </Button>
      </div>
    </QuizPlayerScreen>
  );
}
